/*
 * PARACELART theme enhancements v1.2.0 (October 2026)
 * Original code written for PARACELART / CustomHub LLC. Plain JavaScript, no third-party libraries,
 * not copied or derived from any other theme.
 *
 *  1. Order-by countdown in the announcement bar   [data-pa-countdown]
 *  2. Collection filters, instant sort, per-row     [data-pa-collection]
 *  3. Shop the look points                          [data-pa-look]
 *  4. Before / after slider                         [data-pa-ba]
 */
(() => {
  'use strict';
  document.documentElement.classList.add('pa-js');

  const store = {
    get(key) { try { return window.localStorage.getItem(key); } catch (e) { return null; } },
    set(key, value) { try { window.localStorage.setItem(key, value); } catch (e) { /* storage unavailable */ } }
  };
  const mqMobile = window.matchMedia('(max-width: 760px)');
  const mqDrawer = window.matchMedia('(max-width: 1100px)');

  /* Keep a CSS variable with the sticky header height so the collection toolbar can sit under it. */
  function syncHeaderHeight() {
    const header = document.querySelector('.site-header');
    const height = header ? Math.round(header.getBoundingClientRect().height) : 0;
    document.documentElement.style.setProperty('--pa-header-h', height + 'px');
  }

  /* ---------------------------------------------------------------- 1. Countdown */
  function zoneOffset(timestamp, timeZone) {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    }).formatToParts(new Date(timestamp));
    const v = {};
    parts.forEach(p => { v[p.type] = p.value; });
    const asUTC = Date.UTC(+v.year, +v.month - 1, +v.day, +v.hour % 24, +v.minute, +v.second);
    return asUTC - Math.floor(timestamp / 1000) * 1000;
  }

  // "2026-12-01 23:59" in the chosen time zone -> UTC milliseconds
  function deadlineToTime(text, timeZone) {
    const m = String(text || '').trim().match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{1,2}):(\d{2}))?/);
    if (!m) return NaN;
    const y = +m[1], mo = +m[2] - 1, d = +m[3];
    const h = m[4] === undefined ? 23 : +m[4];
    const mi = m[5] === undefined ? 59 : +m[5];
    const wall = Date.UTC(y, mo, d, h, mi, 0);
    let guess = wall;
    try {
      for (let i = 0; i < 3; i++) guess = wall - zoneOffset(guess, timeZone);
    } catch (e) {
      guess = new Date(y, mo, d, h, mi).getTime(); // unknown zone: fall back to the shopper's clock
    }
    return guess;
  }

  function initCountdown(bar) {
    if (bar.dataset.paReady) return;
    bar.dataset.paReady = '1';
    const end = deadlineToTime(bar.dataset.deadline, bar.dataset.timezone || 'America/New_York');
    const timer = bar.querySelector('.pa-cd__timer');
    const units = {};
    bar.querySelectorAll('[data-pa-unit]').forEach(el => { units[el.dataset.paUnit] = el; });
    const withSeconds = !!units.seconds;
    let handle = null;

    function expire() {
      if (handle) clearInterval(handle);
      const fallback = bar.dataset.fallback || '';
      if (fallback) {
        bar.classList.remove('announcement--countdown');
        bar.textContent = fallback;
      } else {
        bar.hidden = true;
      }
    }

    function tick() {
      const left = end - Date.now();
      if (!(left > 0)) { expire(); return; }
      const s = Math.floor(left / 1000);
      const days = Math.floor(s / 86400);
      const hours = Math.floor((s % 86400) / 3600);
      const minutes = Math.floor((s % 3600) / 60);
      const seconds = s % 60;
      if (units.days) units.days.textContent = String(days);
      if (units.hours) units.hours.textContent = String(hours).padStart(2, '0');
      if (units.minutes) units.minutes.textContent = String(minutes).padStart(2, '0');
      if (units.seconds) units.seconds.textContent = String(seconds).padStart(2, '0');
      if (timer) {
        timer.hidden = false;
        timer.setAttribute('aria-label', `${days} days, ${hours} hours and ${minutes} minutes left to order`);
      }
    }

    if (isNaN(end)) { return; } // invalid date: the message still shows, without numbers
    tick();
    handle = setInterval(tick, withSeconds ? 1000 : 15000);
  }

  /* ---------------------------------------------------------------- 2. Collection */
  function initCollection(section) {
    if (section.dataset.paReady) return;
    section.dataset.paReady = '1';
    const sectionId = section.dataset.sectionId;
    const form = section.querySelector('[data-pa-facets]');
    if (!form) return;
    const defaultCols = section.dataset.defaultCols || '3';
    let controller = null;
    let priceTimer = null;

    /* Products per row */
    function applyColumns() {
      const grid = section.querySelector('[data-pa-grid]');
      if (!grid) return;
      const desktop = store.get('pa-cols-desktop') || defaultCols;
      const mobile = store.get('pa-cols-mobile') || '2';
      grid.dataset.cols = desktop;
      grid.dataset.mcols = mobile;
      section.querySelectorAll('[data-pa-cols]').forEach(btn => {
        const value = btn.dataset.paCols;
        const isMobileBtn = btn.classList.contains('is-mobile');
        btn.setAttribute('aria-pressed', String(isMobileBtn ? value === mobile : value === desktop));
      });
    }
    section.addEventListener('click', event => {
      const btn = event.target.closest('[data-pa-cols]');
      if (!btn) return;
      store.set(btn.classList.contains('is-mobile') ? 'pa-cols-mobile' : 'pa-cols-desktop', btn.dataset.paCols);
      applyColumns();
    });

    /* Filter drawer (tablet and phone) */
    const panel = section.querySelector('[data-pa-panel]');
    const scrim = section.querySelector('.pa-filter-scrim');
    const opener = section.querySelector('[data-pa-drawer-open]');
    function openDrawer() {
      if (!panel || !mqDrawer.matches) return;
      panel.classList.add('is-open');
      if (scrim) scrim.hidden = false;
      document.documentElement.classList.add('pa-lock');
      opener?.setAttribute('aria-expanded', 'true');
      panel.querySelector('[data-pa-drawer-close]')?.focus();
    }
    function closeDrawer(returnFocus = true) {
      if (!panel || !panel.classList.contains('is-open')) return;
      panel.classList.remove('is-open');
      if (scrim) scrim.hidden = true;
      document.documentElement.classList.remove('pa-lock');
      opener?.setAttribute('aria-expanded', 'false');
      if (returnFocus) opener?.focus();
    }
    opener?.addEventListener('click', openDrawer);
    section.addEventListener('click', event => {
      if (event.target.closest('[data-pa-drawer-close]')) closeDrawer();
    });
    section.addEventListener('keydown', event => {
      if (event.key === 'Escape') closeDrawer();
    });
    mqDrawer.addEventListener?.('change', () => closeDrawer(false));

    /* Remember which filter groups the shopper closed */
    function filterStates() {
      const closed = new Set();
      section.querySelectorAll('[data-pa-filter]').forEach(d => { if (!d.open) closed.add(d.dataset.paFilter); });
      return closed;
    }
    function restoreFilterStates(closed) {
      section.querySelectorAll('[data-pa-filter]').forEach(d => { if (closed.has(d.dataset.paFilter)) d.open = false; });
    }

    function formUrl() {
      const params = new URLSearchParams();
      new FormData(form).forEach((value, key) => {
        if (String(value).trim() !== '') params.append(key, value);
      });
      const query = params.toString();
      return form.getAttribute('action') + (query ? '?' + query : '');
    }

    async function render(url, push = true) {
      const results = section.querySelector('[data-pa-results]');
      const closed = filterStates();
      const focusedName = document.activeElement && section.contains(document.activeElement) ? document.activeElement.name : null;
      const focusedValue = focusedName ? document.activeElement.value : null;
      if (controller) controller.abort();
      controller = new AbortController();
      results?.setAttribute('aria-busy', 'true');
      try {
        const fetchUrl = new URL(url, location.origin);
        fetchUrl.searchParams.set('section_id', sectionId);
        const response = await fetch(fetchUrl.toString(), { signal: controller.signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error('Section request failed');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const swap = selector => {
          const next = html.querySelector(selector);
          const current = section.querySelector(selector);
          if (next && current) current.innerHTML = next.innerHTML;
        };
        swap('[data-pa-results]');
        swap('[data-pa-filters]');
        const nextCount = html.querySelector('[data-pa-count]');
        const count = section.querySelector('[data-pa-count]');
        if (nextCount && count) count.textContent = nextCount.textContent;
        const nextOpener = html.querySelector('[data-pa-drawer-open]');
        if (nextOpener && opener) opener.innerHTML = nextOpener.innerHTML;
        restoreFilterStates(closed);
        applyColumns();
        if (push) history.pushState({ paCollection: sectionId }, '', url);
        if (focusedName) {
          const again = [...section.querySelectorAll('[name]')].find(el => el.name === focusedName && (el.type !== 'checkbox' || el.value === focusedValue));
          again?.focus({ preventScroll: true });
        }
      } catch (error) {
        if (error.name !== 'AbortError') window.location.href = url; // fall back to a normal page load
      } finally {
        section.querySelector('[data-pa-results]')?.removeAttribute('aria-busy');
      }
    }

    form.addEventListener('change', event => {
      const target = event.target;
      if (target.matches('[data-pa-price]')) return; // handled with a short pause below
      if (target.matches('input[type="checkbox"], select')) render(formUrl());
    });
    form.addEventListener('input', event => {
      if (!event.target.matches('[data-pa-price]')) return;
      clearTimeout(priceTimer);
      priceTimer = setTimeout(() => render(formUrl()), 700);
    });
    form.addEventListener('submit', event => {
      event.preventDefault();
      if (mqDrawer.matches && panel?.classList.contains('is-open')) { closeDrawer(); render(formUrl()); return; }
      render(formUrl());
    });
    section.addEventListener('click', event => {
      const link = event.target.closest('[data-pa-link], .pagination a');
      if (!link || !section.contains(link)) return;
      event.preventDefault();
      const isPage = !!link.closest('.pagination');
      render(link.href).then(() => {
        if (isPage) section.querySelector('.pa-toolbar')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
    window.addEventListener('popstate', () => {
      if (location.pathname === new URL(form.action, location.origin).pathname) render(location.href, false);
    });

    applyColumns();
  }

  /* ---------------------------------------------------------------- 3. Shop the look */
  function initLook(section) {
    if (section.dataset.paReady) return;
    section.dataset.paReady = '1';
    const dots = [...section.querySelectorAll('[data-pa-dot]')];
    const pops = [...section.querySelectorAll('[data-pa-pop]')];
    const items = [...section.querySelectorAll('[data-pa-item]')];

    function setActive(index) {
      dots.forEach(d => d.classList.toggle('is-active', d.dataset.paDot === index));
      items.forEach(i => i.classList.toggle('is-active', i.dataset.paItem === index));
    }
    function closeAll() {
      pops.forEach(p => { p.hidden = true; });
      dots.forEach(d => d.setAttribute('aria-expanded', 'false'));
      setActive(null);
    }
    function open(index) {
      closeAll();
      const dot = dots.find(d => d.dataset.paDot === index);
      const pop = pops.find(p => p.dataset.paPop === index);
      setActive(index);
      if (mqMobile.matches) {
        items.find(i => i.dataset.paItem === index)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        return;
      }
      if (pop) pop.hidden = false;
      dot?.setAttribute('aria-expanded', 'true');
    }

    dots.forEach(dot => dot.addEventListener('click', event => {
      event.stopPropagation();
      const index = dot.dataset.paDot;
      if (dot.getAttribute('aria-expanded') === 'true') closeAll(); else open(index);
    }));
    items.forEach(item => {
      item.addEventListener('mouseenter', () => setActive(item.dataset.paItem));
      item.addEventListener('mouseleave', () => setActive(null));
    });
    document.addEventListener('click', event => { if (!section.contains(event.target)) closeAll(); });
    section.addEventListener('click', event => {
      if (!event.target.closest('[data-pa-pop], [data-pa-dot]')) closeAll();
    });
    section.addEventListener('keydown', event => {
      if (event.key === 'Escape') {
        const openDot = dots.find(d => d.getAttribute('aria-expanded') === 'true');
        closeAll();
        openDot?.focus();
      }
    });
    section.addEventListener('shopify:block:select', event => {
      const dot = event.target.closest('[data-pa-dot]');
      if (dot) open(dot.dataset.paDot);
    });
  }

  /* ---------------------------------------------------------------- 4. Before / after */
  function initCompare(frame) {
    if (frame.dataset.paReady) return;
    frame.dataset.paReady = '1';
    const range = frame.querySelector('[data-pa-ba-range]');
    const set = value => {
      const v = Math.min(100, Math.max(0, Number(value)));
      frame.style.setProperty('--pos', v + '%');
      if (range && Number(range.value) !== Math.round(v)) range.value = Math.round(v);
    };
    const fromPointer = event => {
      const box = frame.getBoundingClientRect();
      set(((event.clientX - box.left) / box.width) * 100);
    };
    let dragging = false;
    frame.addEventListener('pointerdown', event => {
      if (event.button !== undefined && event.button !== 0) return;
      dragging = true;
      frame.classList.add('is-dragging');
      try { frame.setPointerCapture(event.pointerId); } catch (e) { /* older browsers */ }
      fromPointer(event);
    });
    frame.addEventListener('pointermove', event => { if (dragging) fromPointer(event); });
    const stop = () => { dragging = false; frame.classList.remove('is-dragging'); };
    frame.addEventListener('pointerup', stop);
    frame.addEventListener('pointercancel', stop);
    range?.addEventListener('input', () => set(range.value));
  }

  function init(root = document) {
    syncHeaderHeight();
    root.querySelectorAll('[data-pa-countdown]').forEach(initCountdown);
    root.querySelectorAll('[data-pa-collection]').forEach(initCollection);
    root.querySelectorAll('[data-pa-look]').forEach(initLook);
    root.querySelectorAll('[data-pa-ba]').forEach(initCompare);
  }

  window.addEventListener('resize', syncHeaderHeight, { passive: true });
  document.addEventListener('shopify:section:load', event => init(event.target));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init()); else init();
})();
